import { Hamburger } from "@doodl/slate";

import Drawer from "./components/drawer";
import MainMenu from "./components/main-menu";
import Search from "./components/search";
import SwitchingButton from "./components/switching-button";

const ICON_SELECTORS = Object.freeze({
    iBurger: 'i.fa-bars',
    iGlass: 'i.fa-search',
    svgBurger: 'svg.fa-bars',
    svgGlass: 'svg.fa-magnifying-glass',
})

const DRAWER_SIGNATURES = Object.freeze({
    nav: 'drawer-nav',
    search: 'drawer-search'
})

export default class SearchAndNavDrawer extends Drawer {
    #burger = null
    #nav = null
    #search = null
    #searchNav = null
    #signature = null

    constructor() {
        super()

        this.#nav = new MainMenu(this)
        this.resizeCallback = this.#nav.toggleHoverEffects

        this.#search = new Search(this)

        /**
         * Left button
         */
        this.#searchNav = new SwitchingButton('button.search-nav', 
            { 
                signature: DRAWER_SIGNATURES.search, 
                callback: this.openSearch, 
                mainIconSelector: ICON_SELECTORS.iGlass, 
                altIconSelector: ICON_SELECTORS.svgGlass,
                title: 'Site Search'
            },
            { 
                signature: DRAWER_SIGNATURES.nav, 
                callback: this.openNav, 
                mainIconSelector: ICON_SELECTORS.iBurger, 
                altIconSelector: ICON_SELECTORS.svgBurger,
                title: 'Navigation Menu'
            },
            'searchNav',
            this,
            true
        )

        /**
         * Right button
         */
        this.#burger = new Hamburger({
            selector: 'button.hamburger',
            onOpen: this.onOpenBurger,
            onClose: this.onCloseBurger
        })
        
        const burgerElement = this.getElementBySelector('button.hamburger')
        this.registerNewElement('navBurger', burgerElement)
    }

    /**
     * Event handler for opening nav drawer via `this.#searchNav`
     */
    openNav = () => {
        this.#toggleDrawerContent(DRAWER_SIGNATURES.nav, this.#burger.close, this.#nav.openMenu)
    }

    /**
     * Event handler for opening search drawer via `this.#searchNav`
     */
    openSearch = () => {
        this.#toggleDrawerContent(DRAWER_SIGNATURES.search, this.#nav.closeMenu, this.#search.open)
    }

    /**
     * Toggle drawer content based on signature
     * 
     * IMPORTANT: Sequence should be maintained as:
     *  1. Hide previous drawer's content
     *  2. Set new signature
     *  3. Open drawer element
     *  3. Show next drawer's content
     * 
     * @param {string} signature - next drawer content's signature
     * @param {function} closePrevious - callback to hide previous drawer's content
     * @param {function} openNext - callback to show next drawer's content
     */
    #toggleDrawerContent = (signature, closePrevious, openNext) => {
        if (!typeof closePrevious === 'function') {
            throw new Error('closePrevious must be a function')
        }

        if (!typeof openNext === 'function') {
            throw new Error('openNext must be a function')
        }

        closePrevious()

        /**
         * Set next drawer content's signature as
         * `this.onOpenBurger` will refer to this value.
         */
        this.#signature = signature
        this.#burger.open()

        openNext()
    }

    /**
     * Opens respective drawer when called by `this.#searchNav.onClick`.
     * Otherwise, always opens the nav drawer.
     */
    onOpenBurger = () => {
        switch (this.#signature) {
            case DRAWER_SIGNATURES.nav:
            case DRAWER_SIGNATURES.search:
                this.openDrawer()
                break
            default:
                this.openNav()
        }

        this.#burgerStateUpdate(true)
    }

    onCloseBurger = () => {
        // reset state
        this.#signature = null

        // reset drawer state
        this.closeDrawer()

        // reset switching button state
        this.#searchNav.reset()

        // reset drawer content state
        this.#search.close()
        this.#nav.closeMenu()

        // reset burger menu state
        this.#burgerStateUpdate(false)
    }

    #burgerStateUpdate = (isOpen) => {
        const burger = this.getRegisteredElement('navBurger')

        burger.setAttribute('title', isOpen ? 'Close' : 'Navigation Menu')
        burger.setAttribute('aria-label', isOpen ? 'Close' : 'Navigation Menu')
    }
}